import { Material, Mesh, Object3D } from 'three';

import { Tiled3DModelHandle } from './geodata/3d-tiled-models/tiled-3d-model-handle';
import { Cesium3dTilesPointsMaterial } from './geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { CesiumPointsMaterial } from './geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { ViewSettings } from './view-settings';

type OriginalOpacity = { transparent: boolean; opacity: number };

/**
 * The tiled model's materials made translucent, to restore when it's opaque again. The tiles
 * renderer detaches the tiles that aren't shown, which a traversal then doesn't reach, and
 * reattaches them as they were.
 */
const translucentMaterials = new Set<Material>();

function setMaterialOpacity(material: Material, opacity: number) {
  const original = (material.userData.viewOpacity ??= {
    transparent: material.transparent,
    opacity: material.opacity,
  }) as OriginalOpacity;

  if (opacity < 1 && !translucentMaterials.has(material)) {
    translucentMaterials.add(material);
    material.addEventListener('dispose', () => translucentMaterials.delete(material));
  }

  material.opacity = original.opacity * opacity;
  const transparent = original.transparent || opacity < 1;
  if (material.transparent !== transparent) {
    material.transparent = transparent;
    material.needsUpdate = true;
  }
}

function setObjectOpacity(root: Object3D, opacity: number) {
  root.traverse((object) => {
    if (object instanceof Mesh) {
      const materials: Material[] = Array.isArray(object.material)
        ? object.material
        : [object.material];
      materials.forEach((material) => setMaterialOpacity(material, opacity));
    }
  });
}

/**
 * Applies the tiled model's and point cloud's opacity from the view settings. Call every frame:
 * tiles that load later get the opacity too.
 */
export function applyLayerOpacity() {
  const { opacity } = ViewSettings.instance;

  CesiumPointsMaterial.instance.opacity = opacity.pointCloud;
  Cesium3dTilesPointsMaterial.instance.opacity = opacity.pointCloud;

  // Gone over every frame while translucent, for the tiles loaded or reattached since
  const tiledModel = Tiled3DModelHandle.instance.getActiveTiledModel();
  if (tiledModel && opacity.tiledModel < 1) {
    setObjectOpacity(tiledModel, opacity.tiledModel);
  } else if (translucentMaterials.size > 0) {
    translucentMaterials.forEach((material) => setMaterialOpacity(material, 1));
    translucentMaterials.clear();
  }
}
